/**
 * storage.js — unified async KV surface for the customer app.
 *
 * On native (iOS / Android) the API is backed by Capacitor Preferences.
 * That storage on iOS is written to a group-scoped file and, on Android,
 * to SharedPreferences — both survive app restarts and are per-app
 * private. This is NOT full Keychain / Keystore encryption; the OS
 * provides sandbox isolation between apps but does not encrypt at rest.
 * For a customer-facing EV app this is a materially better position
 * than the JS-accessible `localStorage` on the same device (which any
 * WebView-injected script could read), which is why the AuthContext
 * boot flow prefers it.
 *
 * On web the abstraction proxies `window.localStorage` (with an
 * in-memory fallback for private-mode Safari and SSR) so nothing about
 * the existing web deployment changes. The web tokenStorage.js in
 * `src/auth/` is unchanged and continues to own the sync path used by
 * axios interceptors — see `src/auth/tokenStorage.js` for the shared
 * key names and the migration story.
 *
 * All exported functions are Promise-based to keep the native backend
 * shape uniform. Callers that need the current synchronous localStorage
 * read on web still use `tokenStorage.js` directly; this async layer
 * is for state that either doesn't exist yet or is being migrated to
 * secure storage on native.
 */
import { isNative } from './platform.js';

let _prefs = null;

async function preferences() {
  if (_prefs) return _prefs;
  const mod = await import('@capacitor/preferences');
  _prefs = mod.Preferences;
  return _prefs;
}

// In-memory fallback for environments where window.localStorage is
// unavailable (SSR, disabled storage, private-mode Safari quota
// throws). Deliberately shaped like the localStorage API so the
// backingStore() probe can return either object.
const memory = new Map();

function webBackingStore() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.getItem('__gc.probe__');
      return {
        getItem: (k) => window.localStorage.getItem(k),
        setItem: (k, v) => window.localStorage.setItem(k, v),
        removeItem: (k) => window.localStorage.removeItem(k),
      };
    }
  } catch { /* private-mode Safari, disabled storage, quota — fall through */ }
  return {
    getItem: (k) => (memory.has(k) ? memory.get(k) : null),
    setItem: (k, v) => memory.set(k, String(v)),
    removeItem: (k) => memory.delete(k),
  };
}

/**
 * Read a value. Returns `null` when the key is absent (never throws).
 */
export async function get(key) {
  if (isNative()) {
    try {
      const p = await preferences();
      const { value } = await p.get({ key });
      return value ?? null;
    } catch {
      return null;
    }
  }
  return webBackingStore().getItem(key);
}

/**
 * Write a value. Coerces to string — anything else must be JSON.stringify'd
 * by the caller. `null` deletes.
 */
export async function set(key, value) {
  if (value == null) return remove(key);
  const stringValue = typeof value === 'string' ? value : String(value);
  if (isNative()) {
    try {
      const p = await preferences();
      await p.set({ key, value: stringValue });
    } catch { /* best-effort; caller cannot recover */ }
    return;
  }
  try { webBackingStore().setItem(key, stringValue); } catch { /* quota */ }
}

/** Idempotent delete. */
export async function remove(key) {
  if (isNative()) {
    try {
      const p = await preferences();
      await p.remove({ key });
    } catch { /* best-effort */ }
    return;
  }
  try { webBackingStore().removeItem(key); } catch { /* ignore */ }
}

/**
 * Delete a list of keys — used at logout to wipe both the auth token
 * pair AND anything else the AuthProvider decides should be scrubbed
 * (see AuthContext.jsx). Does not touch keys outside the list.
 */
export async function removeMany(keys) {
  if (!Array.isArray(keys)) return;
  for (const k of keys) {
    // Serial delete so a failure on one key doesn't leak into the next.
    await remove(k);
  }
}

/**
 * Convenience helper for JSON objects. Silently returns `fallback` on
 * parse failure so a corrupted preferences file doesn't crash boot.
 */
export async function getJSON(key, fallback = null) {
  const raw = await get(key);
  if (raw == null) return fallback;
  try { return JSON.parse(raw); } catch { return fallback; }
}

export async function setJSON(key, value) {
  if (value == null) return remove(key);
  return set(key, JSON.stringify(value));
}
